import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBell,
  faTimes,
  faRefresh,
  faCheck,
  faExclamation,
  faInfoCircle,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';

const NotificationPanel = ({ 
  notifications, 
  onClose, 
  onNotificationClick, 
  onRefresh 
}) => {
  
  const getNotificationIcon = (type) => {
    const icons = {
      task_assigned: faCheck,
      task_updated: faInfoCircle,
      task_completed: faCheck,
      task_due_soon: faClock,
      task_overdue: faExclamation
    };
    return icons[type] || faInfoCircle;
  };

  const getNotificationColor = (type) => {
    const colors = {
      task_assigned: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30',
      task_updated: 'text-indigo-600 bg-indigo-100 dark:bg-indigo-900/30',
      task_completed: 'text-green-600 bg-green-100 dark:bg-green-900/30',
      task_due_soon: 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30',
      task_overdue: 'text-red-600 bg-red-100 dark:bg-red-900/30'
    };
    return colors[type] || 'text-gray-600 bg-gray-100 dark:bg-gray-900/30';
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const panelVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.95,
      transformOrigin: 'top right'
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      transformOrigin: 'top right'
    },
    exit: { 
      opacity: 0, 
      scale: 0.95,
      transformOrigin: 'top right'
    }
  };

  return (
    <motion.div
      className="notification-panel"
      variants={panelVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      transition={{ duration: 0.2 }}
    >
      {/* Header */}
      <div className="notification-header">
        <div className="flex items-center">
          <FontAwesomeIcon icon={faBell} className="text-indigo-600 mr-2" />
          <h3 className="font-semibold text-gray-900 dark:text-white">
            Notifications
          </h3>
          {notifications.length > 0 && (
            <span className="ml-2 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 text-xs px-2 py-1 rounded-full">
              {notifications.length}
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={onRefresh}
            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
            title="Refresh notifications"
          >
            <FontAwesomeIcon icon={faRefresh} className="text-sm" />
          </button>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
            title="Close"
          >
            <FontAwesomeIcon icon={faTimes} className="text-sm" />
          </button>
        </div>
      </div>

      {/* Notification List */}
      <div className="notification-list">
        {notifications.length === 0 ? (
          <div className="p-8 text-center">
            <FontAwesomeIcon 
              icon={faBell} 
              className="text-4xl text-gray-300 dark:text-gray-600 mb-3" 
            />
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              No new notifications
            </p>
            <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">
              You're all caught up!
            </p>
          </div>
        ) : (
          notifications.map((notification, index) => (
            <motion.div
              key={notification.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className="notification-item"
              onClick={() => onNotificationClick(notification)}
            >
              <div className="flex items-start space-x-3">
                {/* Notification Icon */}
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${getNotificationColor(notification.notification_type)}`}>
                  <FontAwesomeIcon 
                    icon={getNotificationIcon(notification.notification_type)} 
                    className="text-sm"
                  />
                </div>
                
                {/* Notification Content */}
                <div className="flex-1 min-w-0">
                  <p className="notification-title">
                    {notification.title}
                  </p>
                  
                  {notification.message && (
                    <p className="notification-message">
                      {notification.message}
                    </p>
                  )}
                  
                  <div className="flex items-center justify-between mt-2">
                    <p className="notification-time">
                      {formatTimeAgo(notification.created_at)}
                    </p>
                    
                    {notification.task_title && (
                      <p className="text-xs text-indigo-600 dark:text-indigo-400 truncate ml-2">
                        {notification.task_title}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
      
      {/* Footer */}
      {notifications.length > 0 && (
        <div className="p-3 border-t border-gray-200 dark:border-gray-700 text-center">
          <button className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium">
            View All Notifications
          </button>
        </div>
      )}
    </motion.div>
  );
};

export default NotificationPanel;